import { Box, Button, Grid, Modal, Paper, Skeleton, Stack, Tabs, Text } from "@mantine/core"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router"
import useSwr from "swr"
import { OrderApi } from "../../apis"
import { PageLayout } from "../../components/layout"
import { BreadcrumbNav } from "../../components/odoo/breadcrumb-nav"
import { CancelConfirmedOrderButton } from "../../components/order-cancel/CancelConfirmedOrderButton"
import { DeliveryLocationMap, isDeliveryLocationEditable } from "../../components/order/delivery-location-map"
import { OrderStepper } from "../../components/order/order-stepper"
import { useSalesActions } from "../../components/order/order-actions"
import { usePermission } from "../../components/permission"
import { ActivityLog } from "../../components/purchase-sales/activity-log"
import { PurchaseSalesConfirmForm, PURCHASE_CONFIRM_MODAL_SIZE } from "../../components/purchase-sales/confirm-form"
import { DetailActionRow } from "../../components/purchase-sales/detail-action-row"
import { DetailGeneralTab } from "../../components/purchase-sales/detail-general-tab"
import { DetailSidebar } from "../../components/purchase-sales/detail-sidebar"
import { PurchaseSalesDynamicDetailForm } from "../../components/purchase-sales/dynamic-detail-form"
import { ListState } from "../../components/ui"
import { buildOrderStatusSteps } from "../../utils/order-status-steps"
import { IAuth } from "../../store/Auth"
import { IGeneral } from "../../store/General"
import classes from "./detail.module.css"

/** PO detail with load, error, not-found and denied states (Ord_States), and the decision row of Ord_PurchaseDetail. */
export function PurchaseSalesDetail() {
  const { t } = useTranslation(["supplierSales", "order", "common"])
  const params = useParams()
  const navigate = useNavigate()
  const [action, setAction] = useState<string[]>([])
  const orderStatus = useSelector((s: { general: IGeneral }) => s.general.orderStatus)
  const myBusinessId = useSelector((s: { auth: IAuth }) => s.auth.user?.currentBusiness?.id)

  const ORD_SO_DETAIL = usePermission("ORD_SO_DETAIL") // BE order/get.ts
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const { stateOf } = useSalesActions()

  const { data, error, isLoading, mutate } = useSwr(
    ORD_SO_DETAIL.hasAccess ? `/ord/order/detail/get.${params.id}` : null,
    async () => await OrderApi.get(params.id as string),
  )

  const frame = (children: React.ReactNode) => (
    <PageLayout
      title={t("supplierSales:purchase.detailTitle")}
      breadcrumb={[
        { to: "/", label: t("common:breadcrumb.home") },
        { to: "/supplier-purchase", label: t("supplierSales:purchase.orderListBreadcrumb") },
        { label: t("supplierSales:purchase.detailTitle") },
      ]}>
      <Box>{children}</Box>
    </PageLayout>
  )

  if (!ORD_SO_DETAIL.hasAccess) return frame(ORD_SO_DETAIL.accessDenied())

  if (isLoading && !data) {
    return frame(
      <Stack gap="md">
        <Skeleton height={96} radius="lg" />
        <Skeleton height={260} radius="lg" />
        <Skeleton height={260} radius="lg" />
      </Stack>,
    )
  }

  const status = (error as { statusCode?: number } | undefined)?.statusCode
  if (status === 404 || (!error && !data)) {
    return frame(
      <ListState
        state="empty"
        title={t("supplierSales:purchase.notFoundTitle")}
        description={t("supplierSales:purchase.notFoundText")}
        action={{ label: t("supplierSales:purchase.backToList"), onClick: () => navigate("/supplier-purchase") }}
      />,
    )
  }
  if (error) {
    return frame(
      <Stack gap="md" align="flex-start">
        <ListState state="error" onRetry={() => mutate()} errorCode={status} />
        <Button variant="default" onClick={() => navigate("/supplier-purchase")}>{t("supplierSales:purchase.backToList")}</Button>
      </Stack>,
    )
  }

  const order = data
  const approve = stateOf("approve", order)
  const decline = stateOf("decline", order)
  const isReceiver = !!myBusinessId && myBusinessId === order.receiverBusiness?.id
  const canEditGps = isDeliveryLocationEditable(order.orderStatus) && (ORD_SAVE.hasAccess || ORD_SEND.hasAccess)

  // Progress ends at «Баталгаажсан»; a declined PO stops at «Татгалзсан», a cancelled one shows «Цуцалсан».
  const { steps, currentStatus, pastEnd } = buildOrderStatusSteps(order, orderStatus, { perspective: "received", upTo: "AUTHORIZED" })
  const translated = steps.map((s) => ({ code: s.code, name: t(`order:orderStatus.${s.code}`, { defaultValue: s.name }) }))

  return (
    <>
      {/* The app shell already pads the page; no second gutter here (canvas: content at the shell edge). */}
      <Box pb="md">
        <BreadcrumbNav
          items={[
            { label: t("common:breadcrumb.home"), to: "/" },
            { label: t("supplierSales:purchase.orderListBreadcrumb"), to: "/supplier-purchase" },
            { label: order.purchaseCode || `PO-${params.id}` },
          ]}
        />
        {/* Compact progress above the content: dark check for done, ringed number for current, grey pending. */}
        <Box mt="md">
          <OrderStepper steps={translated} currentStatus={currentStatus} layout="inline" currentTone="dark" checkDone allDone={pastEnd} />
        </Box>
      </Box>

      <Box pb="md">
        <Box className={classes.grid}>
          <Box className={classes.sidebar}>
            <DetailSidebar data={order} status={currentStatus} />
          </Box>

          <Box className={classes.main}>
            <DetailActionRow
              status={order.orderStatus}
              decisionOpen={approve.applies}
              approveReason={approve.reason}
              declineReason={decline.reason}
              onApprove={() => setAction(["confirm", order, "confirm"])}
              onReject={() => setAction(["reject", order, "reject"])}
              extra={isReceiver ? <CancelConfirmedOrderButton order={order} onSuccess={() => mutate()} /> : undefined}
            />

            <Paper mt="md" radius="lg" withBorder p={0} className={classes.tabsCard}>
              <Tabs defaultValue="lines" keepMounted={false}>
                <Tabs.List px="md" pt="xs">
                  <Tabs.Tab value="lines">{t("supplierSales:purchase.detail.tabs.lines")}</Tabs.Tab>
                  <Tabs.Tab value="general">{t("supplierSales:purchase.detail.tabs.general")}</Tabs.Tab>
                  <Tabs.Tab value="history">{t("supplierSales:purchase.detail.tabs.history")}</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="lines">
                  <PurchaseSalesDynamicDetailForm action={["detail", order]} isSupplier={isReceiver} fit />
                </Tabs.Panel>
                <Tabs.Panel value="general" p="md">
                  <DetailGeneralTab data={order} />
                </Tabs.Panel>
                <Tabs.Panel value="history" p="md">
                  <ActivityLog data={order} />
                </Tabs.Panel>
              </Tabs>
            </Paper>

            {/* 24 columns so the split is the canvas's 15/9 (location ≈ 556, branch ≈ 370 at 1440). */}
            <Grid mt="md" gap="md" columns={24}>
              <Grid.Col span={{ base: 24, lg: 15 }}>
                <DeliveryLocationMap
                  orderId={order.id}
                  latitude={order.deliveryLatitude}
                  longitude={order.deliveryLongitude}
                  fallbackLat={order.receiverBranch?.locationLat}
                  fallbackLng={order.receiverBranch?.locationLng}
                  canEdit={canEditGps}
                  onSaved={() => mutate()}
                  height={240}
                  quiet
                />
              </Grid.Col>
              <Grid.Col span={{ base: 24, lg: 9 }}>
                <Paper radius="lg" withBorder p="md">
                  <Text fz={16} fw={700} mb="sm">{t("order:fields.receiverBranch")}</Text>
                  <Text fz={16} fw={600}>{order.receiverBranch?.name || "-"}</Text>
                  <Text fz={14} c="gray.8" mt={4}>{order.receiverBranch?.branchAddress || "-"}</Text>
                  <Text fz={14} c="gray.8" mt={4}>
                    {t("supplierSales:purchase.detail.branchLocation")}:{" "}
                    <Text span ff="monospace" fz={14} c="dark.9">
                      {typeof order.receiverBranch?.locationLat === "number" && typeof order.receiverBranch?.locationLng === "number"
                        ? `${order.receiverBranch.locationLat}, ${order.receiverBranch.locationLng}`
                        : "-"}
                    </Text>
                  </Text>
                </Paper>
              </Grid.Col>
            </Grid>
          </Box>
        </Box>
      </Box>

      <Modal
        opened={action[0] === "confirm" || action[0] === "reject"}
        onClose={() => setAction([])}
        withCloseButton={false}
        size={PURCHASE_CONFIRM_MODAL_SIZE}
        padding={0}
        centered
        radius="lg"
      >
        <PurchaseSalesConfirmForm action={action[1] as never} setActions={setAction} constant={action[2]} reload={() => mutate()} />
      </Modal>
    </>
  )
}
